import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";
import { activePurchaseSuppliers } from "../utils/purchaseSuppliers";
import { itemMatchesSupplier } from "../utils/supplierIdentity";
import { requisitionTemplateLines } from "../utils/requisitionTemplateLines";
import { marketplaceQuantityError } from "../utils/marketplaceSelection";

export async function loadMarketplaceCatalog(businessUnitId, config) {
  const [itemRes, supplierRes, unitRes, allowedRes, locationRes] = await Promise.all([
    axios.get("/api/purchasing/items", config), axios.get(`/api/purchasing/suppliers/options?business_unit_id=${businessUnitId}`, config),
    axios.get(`/api/purchasing/units?business_unit_id=${businessUnitId}`, config), axios.get("/api/purchasing/user-item-access-rules/allowed-items", config),
    axios.get("/api/purchasing/inventory/locations?include_inactive=false", config),
  ]);
  if (!Array.isArray(allowedRes.data?.allowed_item_ids)) throw new Error("Invalid item access response");
  const allowed = new Set(allowedRes.data.allowed_item_ids.map(Number));
  return { items: (itemRes.data || []).filter((item) => allowed.has(Number(item.id)) && item.is_active !== false && !item.merged_into_item_id),
    suppliers: activePurchaseSuppliers(supplierRes.data), units: unitRes.data || [], locations: locationRes.data || [] };
}

export function marketplaceTemplateLines(template, catalog, { businessUnitId, languageId, t }) {
  if (template.business_unit_id != null && Number(template.business_unit_id) !== Number(businessUnitId)) {
    throw new Error(languageId === "es" ? "La plantilla pertenece a otra empresa." : "The template belongs to another business unit.");
  }
  if (!Array.isArray(template.lines) || !template.lines.length) throw new Error(t("No lines available."));
  const lines = requisitionTemplateLines(template, { ...catalog, languageId, t });
  const seen = new Set();
  for (const line of lines) {
    const item = catalog.items.find((entry) => Number(entry.id) === Number(line.item_id));
    const supplier = catalog.suppliers.find((entry) => Number(entry.id) === Number(line.supplier_id));
    if (!item || !supplier || !itemMatchesSupplier(item, supplier) || seen.has(Number(line.item_id))) {
      throw new Error(languageId === "es" ? "Revisa los artículos y proveedores de la plantilla: deben estar disponibles, vinculados y sin artículos repetidos." : "Review the template items and suppliers: they must be available, linked and have no repeated items.");
    }
    const invalid = marketplaceQuantityError(line, item, catalog.units, languageId, t);
    if (invalid) throw new Error(invalid);
    seen.add(Number(line.item_id));
    Object.assign(line, { item_id: item.id, supplier_id: supplier.id, unit_id: item.unit_id });
  }
  return lines;
}

export const canCreateMarketplaceRequisition = (user) => Number(user?.id) === 1
  || user?.permissions?.["purchasing.requisitions.create"] === true;

export const canConfirmMarketplaceRequisition = (user) => Number(user?.id) === 1 || user?.permissions?.["purchasing.requisitions.confirm"] === true;
export const canEditMarketplaceRequisition = (user, requisition) => Number(user?.id) === 1 || user?.permissions?.["purchasing.requisitions.update"] === true
  || (requisition?.status === "draft" && Number(requisition.requested_by) === Number(user?.id) && canCreateMarketplaceRequisition(user));
export const savedPriceSelection = (line, date) => JSON.stringify([Number(line.item_id), Number(line.supplier_id), Number(line.unit_id), String(date).slice(0, 10)]);

export function marketplaceFromRequisition(requisition, { repeat = false, today, nextDeliveryDate }) {
  const individual = (requisition.lines || []).some((line) => line.destination_location_id && Number(line.destination_location_id) !== Number(requisition.destination_location_id));
  const lines = (requisition.lines || []).map((line) => ({ ...line, item_id: Number(line.item_id), supplier_id: Number(line.supplier_id), quantity: Number(line.quantity),
    display_unit_level: line.display_unit_level || "unit", unit_id: Number(line.unit_id),
    pack_size: line.pack_size || line.item?.pack_size || 1, subunit_id: line.subunit_id || line.item?.subunit_id || null,
    subunit2_id: line.subunit2_id || line.item?.subunit2_id || null, subunit2_size: line.subunit2_size || line.item?.subunit2_size || null,
    destination_location_id: line.destination_location_id || null, destination_location_source: line.destination_location_id ? (individual ? "manual" : "header") : "suggested",
    ...(repeat ? { id: undefined, requisition_id: undefined, unit_price: null } : { _savedPriceSelection: savedPriceSelection(line, requisition.request_date), _savedUnitPrice: Number(line.unit_price) }),
  }));
  return { ...(repeat ? {} : { id: requisition.id, requisition_code: requisition.requisition_code, status: requisition.status, requested_by: requisition.requested_by, expected_updated_at: requisition.updated_at }),
    request_date: repeat ? today : String(requisition.request_date).slice(0, 10),
    expected_delivery_date: repeat && (!requisition.expected_delivery_date || String(requisition.expected_delivery_date).slice(0, 10) < today) ? nextDeliveryDate : requisition.expected_delivery_date || "",
    notes: requisition.notes || "", destination_location_id: individual ? "" : requisition.destination_location_id || "", destination_mode: individual ? "individual" : "general", lines };
}

// Use the existing header -> line autosaves -> draft patch contract. Checkpoints
// retain the header and line identities after a partial failure, so retry updates
// the same requisition rather than creating one requisition per supplier.
export async function saveMarketplaceRequisition({ form, checkpoint, user, confirm = false, review, onPrepared, editing = false }) {
  if (confirm && !canConfirmMarketplaceRequisition(user)) throw new Error("Confirming requisitions requires its own permission.");
  if (new Set(form.lines.map((line) => Number(line.item_id))).size !== form.lines.length) throw new Error("This requisition supports one selection per item. Remove its existing line before changing supplier or presentation.");
  const assertSession = () => {
    const current = readStoredUser();
    if (!(editing ? canEditMarketplaceRequisition(current, form) : canCreateMarketplaceRequisition(current)) || Number(current.id) !== Number(user.id)
      || Number(current.active_business_unit_id) !== Number(user.active_business_unit_id)) {
      throw new Error("Session changed. Reopen the marketplace before saving.");
    }
  };
  const config = {
    headers: { "X-Pilcore-Business-Unit-Id": String(user.active_business_unit_id) },
    pilcoreExpectedUserId: user.id,
  };
  assertSession();
  if (editing) {
    if (!form.id || !form.expected_updated_at) throw new Error("Reopen this requisition before saving.");
    let acknowledgement = {};
    if (confirm) {
      if (typeof review !== "function") throw new Error("Quantity review is required before confirming.");
      const result = await review({ documentId: form.id, date: form.request_date, lines: form.lines });
      if (!result.allowed) return { review_required: true, id: form.id };
      if (result.token) acknowledgement = { quantity_review_token: result.token, quantity_review_acknowledged: true };
    }
    assertSession();
    const response = await axios.put(`/api/purchasing/requisitions/${form.id}`, {
      marketplace_edit: true, expected_updated_at: form.expected_updated_at, status: confirm ? "confirmed" : "draft",
      request_date: form.request_date, expected_delivery_date: form.expected_delivery_date || null,
      notes: form.notes || null, destination_location_id: form.destination_location_id || null, ...acknowledgement,
      lines: form.lines.map((line) => ({ id: line.id || null, item_id: line.item_id, supplier_id: line.supplier_id, quantity: line.quantity,
        unit_id: line.unit_id, display_unit_level: line.display_unit_level || "unit", pack_size: line.pack_size, subunit_id: line.subunit_id,
        subunit2_id: line.subunit2_id, subunit2_size: line.subunit2_size, unit_price: line.unit_price,
        destination_location_id: line.destination_location_source === "suggested" ? null : line.destination_location_id || form.destination_location_id || null,
        destination_location_source: line.destination_location_source, notes: line.notes || null })),
    }, config);
    return response.data;
  }
  if (checkpoint.header) {
    // A line may have committed even if its response was lost. Reconcile its
    // server identity before removing products from a retried cart.
    const response = await axios.get(`/api/purchasing/requisitions/${checkpoint.header.id}`, config);
    for (const line of response.data.lines || []) checkpoint.lines[line.item_id] = line.id;
  }
  if (!checkpoint.header) {
    const response = await axios.post("/api/purchasing/requisitions", {
      request_date: form.request_date, destination_location_id: form.destination_location_id || null,
      notes: form.notes || null,
    }, config);
    checkpoint.header = response.data;
  }
  const id = checkpoint.header.id;
  const currentItems = new Set(form.lines.map((line) => String(line.item_id)));
  for (const [itemId, lineId] of Object.entries(checkpoint.lines)) {
    if (!currentItems.has(itemId)) {
      assertSession();
      await axios.delete(`/api/purchasing/requisitions/requisition-lines/${lineId}`, config);
      delete checkpoint.lines[itemId];
    }
  }
  // Set the pricing date before resolving lines, including on a retry.
  assertSession();
  await axios.patch(`/api/purchasing/requisitions/${id}`, {
    request_date: form.request_date, destination_location_id: form.destination_location_id || null,
    expected_delivery_date: form.expected_delivery_date || null, notes: form.notes || null,
  }, config);
  const savedLines = [];
  for (const line of form.lines) {
    assertSession();
    const response = await axios.post("/api/purchasing/requisitions/requisition-lines/autosave", {
      requisition_id: id, line_id: checkpoint.lines[line.item_id] || null,
      item_id: line.item_id, supplier_id: line.supplier_id, quantity: line.quantity,
      unit_id: line.unit_id, display_unit_level: line.display_unit_level || "unit",
      pack_size: line.pack_size || 1, subunit_id: line.subunit_id || null,
      subunit2_id: line.subunit2_id || null, subunit2_size: line.subunit2_size || null,
      unit_price: line.unit_price ?? 0,
      destination_location_id: line.destination_location_source === "suggested" ? null : line.destination_location_id || form.destination_location_id || null,
      destination_location_source: line.destination_location_source || (form.destination_location_id ? "header" : null),
      notes: line.notes || null,
    }, config);
    checkpoint.lines[line.item_id] = response.data.id;
    savedLines.push({ ...line, ...response.data, _marketplaceKey: line._marketplaceKey });
  }
  assertSession();
  let acknowledgement = {};
  if (confirm) {
    if (typeof review !== "function") throw new Error("Quantity review is required before creating the requisition.");
    onPrepared?.({ id, lines: savedLines });
    const result = await review({ documentId: id, lines: savedLines, date: form.request_date });
    if (!result.allowed) return { review_required: true, id, requisition_code: checkpoint.header.requisition_code };
    acknowledgement = { quantity_review_check: true, ...(result.token ? { quantity_review_token: result.token, quantity_review_acknowledged: true } : {}) };
  }
  assertSession();
  const response = await axios.patch(`/api/purchasing/requisitions/${id}`, { status: confirm ? "confirmed" : "draft", ...acknowledgement }, config);
  return response.data;
}
